import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import * as Icons from "lucide-react";
import {
  useResource, PageTitle, Loading, ErrorState, EmptyState, Pill, DemoBadge,
  STATUS_TONE, fmtDate, fmtMoney,
} from "../../components/app/Kit";

export default function Customers() {
  const { data, state, error, reload } = useResource("/customers");
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState("all");
  const q = params.get("q") || "";

  const rows = useMemo(() => {
    let list = data || [];
    if (q) {
      const n = q.toLowerCase();
      list = list.filter((c) =>
        [c.name, c.email, c.phone, c.company].filter(Boolean).some((v) => v.toLowerCase().includes(n))
      );
    }
    if (status !== "all") list = list.filter((c) => c.status === status);
    return list;
  }, [data, q, status]);

  return (
    <div data-testid="customers-page">
      <PageTitle
        testId="customers-title"
        title="Customers"
        sub="One record per customer, with full history, revenue and payment status."
        action={<DemoBadge />}
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 sm:max-w-xs">
          <Icons.Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-elv-dim" />
          <input
            value={q}
            onChange={(e) => setParams(e.target.value ? { q: e.target.value } : {})}
            placeholder="Search name, phone, email, company"
            data-testid="customers-search"
            className="w-full rounded-md border border-elv-line bg-white/[0.02] py-2 pl-9 pr-3 text-xs text-elv-text outline-none focus:border-elv-green/40"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {["all", "new", "contacted", "qualified", "booked", "won", "lost"].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatus(s)}
              data-testid={`customers-filter-${s}`}
              className={`rounded-md border px-3 py-1.5 text-[11px] uppercase tracking-[0.12em] ${
                status === s
                  ? "border-elv-green/35 bg-elv-green/[0.08] text-elv-green"
                  : "border-elv-line bg-white/[0.02] text-elv-dim hover:text-elv-text"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {state === "loading" ? <Loading rows={8} testId="customers-loading" /> : null}
      {state === "error" ? <ErrorState message={error} onRetry={reload} testId="customers-error" /> : null}
      {state === "ready" && rows.length === 0 ? (
        <EmptyState
          icon="Users"
          title="No customers yet"
          sub="Customer records are created automatically when a lead is qualified or booked."
          testId="customers-empty"
        />
      ) : null}

      {state === "ready" && rows.length > 0 ? (
        <>
          <div className="glass hidden overflow-hidden rounded-lg lg:block">
            <table data-testid="customers-table" className="w-full text-left text-xs">
              <thead className="border-b border-elv-line text-[10px] uppercase tracking-[0.16em] text-elv-dim">
                <tr>
                  {["Customer", "Company", "Source", "Status", "Owner", "Score", "Revenue", "Payment", "Next follow-up"].map((h) => (
                    <th key={h} className="px-4 py-3 font-normal">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.id} data-testid={`customer-row-${c.id}`} className="border-b border-white/[0.04] hover:bg-white/[0.02]">
                    <td className="px-4 py-3">
                      <Link
                        to={`/app/customers/${c.id}`}
                        data-testid={`customer-link-${c.id}`}
                        className="font-medium text-elv-text hover:text-elv-green"
                      >
                        {c.name}
                      </Link>
                      <p className="mt-1 text-[11px] text-elv-dim">{c.email}</p>
                    </td>
                    <td className="px-4 py-3 text-elv-dim">{c.company}</td>
                    <td className="px-4 py-3 text-elv-dim">{c.source}</td>
                    <td className="px-4 py-3">
                      <Pill tone={STATUS_TONE[c.status]}>{c.status}</Pill>
                    </td>
                    <td className="px-4 py-3 text-elv-dim">{c.assigned_to}</td>
                    <td className="px-4 py-3 font-mono text-elv-text">{c.lead_score}</td>
                    <td className="px-4 py-3 text-elv-text">{fmtMoney(c.revenue)}</td>
                    <td className="px-4 py-3">
                      <Pill tone={STATUS_TONE[c.payment_status] || "dim"}>{c.payment_status}</Pill>
                    </td>
                    <td className="px-4 py-3 text-elv-dim">{fmtDate(c.next_follow_up)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div data-testid="customers-cards" className="grid gap-2 lg:hidden">
            {rows.map((c) => (
              <Link
                key={c.id}
                to={`/app/customers/${c.id}`}
                data-testid={`customer-card-${c.id}`}
                className="glass glass-hover block rounded-lg p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <p className="text-sm font-medium text-elv-text">{c.name}</p>
                  <Pill tone={STATUS_TONE[c.status]}>{c.status}</Pill>
                </div>
                <p className="mt-2 text-[11px] text-elv-dim">
                  {c.email} · {c.phone}
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span className="text-[11px] text-elv-text">{fmtMoney(c.revenue)}</span>
                  <Pill tone={STATUS_TONE[c.payment_status] || "dim"}>{c.payment_status}</Pill>
                  <span className="font-mono text-[11px] text-elv-dim">score {c.lead_score}</span>
                </div>
              </Link>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}
